import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuthStore } from '@/stores/auth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { toast } from '@/components/ui/toaster'
import { Loader2 } from 'lucide-react'

export default function RegisterPage() {
    const { register } = useAuthStore()
    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [tenantName, setTenantName] = useState('')
    const [isLoading, setIsLoading] = useState(false)

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setIsLoading(true)

        try {
            await register({ email, password, name, tenantName })
            toast({ title: 'Welcome!', description: 'Your account has been created.' })
        } catch (error: any) {
            toast({
                title: 'Registration failed',
                description: error.message || 'Could not create account',
                variant: 'destructive',
            })
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div className="animate-in">
            <div className="mb-8">
                <h1 className="text-2xl font-bold mb-2">Create an account</h1>
                <p className="text-muted-foreground">
                    Start your animation project journey
                </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                    <Label htmlFor="name">Your name</Label>
                    <Input
                        id="name"
                        type="text"
                        placeholder="John Doe"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                        autoComplete="name"
                    />
                </div>

                <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                        id="email"
                        type="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        autoComplete="email"
                    />
                </div>

                <div className="space-y-2">
                    <Label htmlFor="tenantName">Organization name</Label>
                    <Input
                        id="tenantName"
                        type="text"
                        placeholder="Acme Studios"
                        value={tenantName}
                        onChange={(e) => setTenantName(e.target.value)}
                        required
                    />
                    <p className="text-xs text-muted-foreground">
                        This will be your workspace name
                    </p>
                </div>

                <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <Input
                        id="password"
                        type="password"
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        autoComplete="new-password"
                        minLength={8}
                    />
                    <p className="text-xs text-muted-foreground">
                        At least 8 characters with uppercase, lowercase, and number
                    </p>
                </div>

                <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Create account
                </Button>
            </form>

            <p className="mt-6 text-center text-sm text-muted-foreground">
                Already have an account?{' '}
                <Link to="/login" className="font-medium text-primary hover:underline">
                    Sign in
                </Link>
            </p>

            <p className="mt-4 text-center text-xs text-muted-foreground">
                By creating an account, you agree to our{' '}
                <a href="#" className="underline hover:text-foreground">
                    Terms of Service
                </a>{' '}
                and{' '}
                <a href="#" className="underline hover:text-foreground">
                    Privacy Policy
                </a>
            </p>
        </div>
    )
}
